import "../styleSheets/dashboardInheritedMessages.css";
import "../styleSheets/buttons.css";
import "../styleSheets/commonElements.css";
import React, { useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { convertUtcToLocal } from "../utilities/utilities";
import { useUser } from "../contexts/UserContext";
import { useDeliveredMessages } from "../graphql/hooks";
import WidgetState from "./WidgetState";
import type { DeliveredMessageListItem } from "../types/message";

function DashboardInheritedMessages() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { user, isAuthenticated } = useUser();
  const loggedInEmail = useMemo(() => {
    return user?.signInDetails?.loginId?.trim().toLowerCase() ?? "";
  }, [user]);

  const {
    data: deliveredMessages = [],
    isLoading,
    isError,
    error,
  } = useDeliveredMessages(
    loggedInEmail,
    isAuthenticated && !!user?.userId && !!loggedInEmail,
  );

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        replace
        state={{ toast: { type: "info", message: t("common.signIn") } }}
      />
    );
  }

  const inheritedMessagesCount = deliveredMessages.length;
  const lastInheritedMessage = deliveredMessages[0];

  function seeAllInherited() {
    navigate("/Panel?selectedTab=inheritedMessages");
  }

  function openDeliveredMessage(msg: DeliveredMessageListItem) {
    if (!msg.id) return;
    navigate(`/InheritedMessageView/${msg.id}`, { state: msg });
  }

  return (
    <section className="section-inherited" aria-labelledby="inherited-messages-heading">
      <section className={"inherited-header"}>
        <div className={"inherited-header-left"}>
          <div className={"icon-container inherited-icon"}></div>
          <div className="inherited-text">
            <h2 id="inherited-messages-heading" className={"inherited-title"}>
              {t("inheritedMessage.inheritedMessages")}
            </h2>
            <div className={"inherited-count"}>
              {isLoading
                ? t("common.loading")
                : isError
                  ? "—"
                  : `${inheritedMessagesCount} ${
                      inheritedMessagesCount === 1
                        ? t("message.message")
                        : t("message.messages").toLocaleString()
                    }`}
            </div>
          </div>
        </div>
      </section>

      <section className={"inherited-center"}>
        <WidgetState
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={!isLoading && inheritedMessagesCount === 0}
          emptyText={t("inheritedMessage.onScreenMessage.noInheritedYet")}
          className="inherited-subtitle"
        >
          <>
            <div className={"inherited-subtitle"}>
              {t("inheritedMessage.latestInherited")}
            </div>

            <div className="last-inherited-section">
              <div className="last-edited-on">
                {t("inheritedMessage.tableColumn.deliveredOn") + " "}
                {convertUtcToLocal(
                  lastInheritedMessage?.deliveredAt ?? null,
                  "ShowDayLong",
                )}
              </div>

              <div className="last-inherited-subject">
                {lastInheritedMessage?.title}
              </div>

              <div className="last-inherited-body">
                {lastInheritedMessage?.bodySnippet ?? ""}
              </div>

              <div className="open-message-button-container">
                <button
                  className="btn btn--transparent"
                  onClick={() =>
                    lastInheritedMessage &&
                    openDeliveredMessage(lastInheritedMessage)
                  }
                  disabled={!lastInheritedMessage}
                >
                  <span className="legacy-section-button">
                    {t("message.openMessage")}
                  </span>
                  <span className="right-arrow-icon red" />
                </button>
              </div>
            </div>
          </>
        </WidgetState>
      </section>
      <section className={"inherited-bottom"}>
        {lastInheritedMessage && (
          <button
            className={"btn btn--white btn--size-md"}
            onClick={seeAllInherited}
          >
            {t("common.seeAll")}
          </button>
        )}
      </section>
    </section>
  );
}

export default DashboardInheritedMessages;
